import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Save, Loader2, AlertCircle, CheckCircle, 
  Plug, TestTube, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { Modal, Badge } from '../../components/ui';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

// Types
interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  version: string;
  description?: string;
  credentialSchema: CredentialField[];
  authConfig: AuthConfig;
}

interface CredentialField {
  name: string;
  label: string;
  type: 'string' | 'password' | 'url' | 'number' | 'boolean' | 'select';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
}

interface AuthConfig {
  type: 'BEARER' | 'BASIC' | 'API_KEY' | 'OAUTH2' | 'CUSTOM';
  headerName?: string;
  headerPrefix?: string;
}

interface ProviderInstance {
  id?: string;
  name: string;
  templateId: string;
  credentials: Record<string, any>;
  isActive: boolean;
  isDefault: boolean;
}

interface ConnectionTestResult {
  success: boolean;
  message: string;
  latency?: number;
  details?: Record<string, any>;
}

interface InstanceConfiguratorProps {
  isOpen: boolean;
  onClose: () => void;
  instance?: ProviderInstance | null;
  templateId?: string;
  instanceId?: string;
  onSaved?: () => void;
}

export const InstanceConfigurator: React.FC<InstanceConfiguratorProps> = ({
  isOpen,
  onClose,
  instance,
  templateId: initialTemplateId,
  instanceId,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId || '');
  const [formData, setFormData] = useState<Record<string, any>>({
    name: '',
    credentials: {},
    isActive: true,
    isDefault: false,
  });
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Load templates
  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ['provider-templates'],
    queryFn: async () => {
      const response = await api.get('/provider-templates');
      // Backend returns { data: [], total: number }
      const result = response.data;
      return result.data || result.templates || result || [];
    },
  });

  // Load instance if instanceId is provided
  const { data: loadedInstance } = useQuery({
    queryKey: ['provider-instance', instanceId],
    queryFn: async () => {
      const response = await api.get(`/provider-instances/${instanceId}`);
      return response.data;
    },
    enabled: !!instanceId && !instance,
  });

  // Get selected template
  const selectedTemplate = templates.find((t: ProviderTemplate) => t.id === selectedTemplateId);

  // Effective instance (from prop or loaded)
  const effectiveInstance = instance || loadedInstance;

  // Initialize form when editing or when initialTemplateId changes
  useEffect(() => {
    if (effectiveInstance) {
      setSelectedTemplateId(effectiveInstance.templateId);
      setFormData({
        name: effectiveInstance.name,
        credentials: effectiveInstance.credentials || {},
        isActive: effectiveInstance.isActive,
        isDefault: effectiveInstance.isDefault,
      });
    } else {
      setSelectedTemplateId(initialTemplateId || '');
      setFormData({
        name: '',
        credentials: {},
        isActive: true,
        isDefault: false,
      });
    }
    setTestResult(null);
  }, [effectiveInstance, isOpen, initialTemplateId]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        ...data,
        templateId: selectedTemplateId,
      };
      
      const existingId = effectiveInstance?.id || instanceId;
      if (existingId) {
        return api.put(`/provider-instances/${existingId}`, payload);
      }
      return api.post('/provider-instances', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances'] });
      onSaved?.();
      onClose();
    },
  });

  // Test connection mutation
  const testMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/provider-instances/test-connection', {
        templateId: selectedTemplateId,
        credentials: formData.credentials,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setTestResult(data);
    },
    onError: (error: any) => {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Falha ao testar conexão',
      });
    },
  });

  // Handle credential change
  const handleCredentialChange = (name: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [name]: value,
      },
    }));
    setTestResult(null); // Reset test result when credentials change
  };

  // Toggle password visibility
  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Handle test connection
  const handleTestConnection = () => {
    setIsTesting(true);
    testMutation.mutate(undefined, {
      onSettled: () => setIsTesting(false),
    });
  };

  // Handle save
  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!selectedTemplateId || !formData.name) return false;
    
    if (selectedTemplate?.credentialSchema) {
      for (const field of selectedTemplate.credentialSchema) {
        if (field.required && !formData.credentials[field.name]) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Check if connection test passed (allow save if editing existing instance or test passed)
  const isEditing = !!(effectiveInstance?.id || instanceId);
  const canSave = isFormValid() && (testResult?.success || isEditing);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Editar Instância' : 'Nova Instância de Provider'}
      size="lg"
    >
      <div className="space-y-6">
        {/* Template Selection - only show when creating new and no templateId provided */}
        {!isEditing && !initialTemplateId && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-surface-200">
              Template do Provider <span className="text-red-400">*</span>
            </label>
            {loadingTemplates ? (
              <div className="flex items-center gap-2 text-surface-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Carregando templates...</span>
              </div>
            ) : (
              <select
                value={selectedTemplateId}
                onChange={(e) => {
                  setSelectedTemplateId(e.target.value);
                  setFormData((prev) => ({ ...prev, credentials: {} }));
                  setTestResult(null);
                }}
                className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              >
                <option value="">Selecione um template...</option>
                {templates.map((template: ProviderTemplate) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.type} v{template.version})
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Template Info */}
        {selectedTemplate && (
          <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700">
            <div className="flex items-center gap-2 mb-2">
              <Plug className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-medium text-white">{selectedTemplate.name}</span>
              <Badge variant="primary">{selectedTemplate.type}</Badge>
              <Badge variant="info">v{selectedTemplate.version}</Badge>
            </div>
            {selectedTemplate.description && (
              <p className="text-xs text-surface-400">{selectedTemplate.description}</p>
            )}
          </div>
        )}

        {/* Instance Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-surface-200">
            Nome da Instância <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: OpenPix Produção"
            className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
          />
        </div>

        {/* Credentials Form */}
        {selectedTemplate?.credentialSchema && selectedTemplate.credentialSchema.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-surface-200 flex items-center gap-2">
              <span>Credenciais</span>
              <Badge variant="warning">Criptografadas</Badge>
            </h3>
            
            <div className="space-y-3">
              {selectedTemplate.credentialSchema.map((field: CredentialField) => (
                <div key={field.name} className="space-y-1.5">
                  <label className="block text-sm font-medium text-surface-300">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  
                  {field.type === 'password' ? (
                    <div className="relative">
                      <input
                        type={showPasswords[field.name] ? 'text' : 'password'}
                        value={formData.credentials[field.name] || ''}
                        onChange={(e) => handleCredentialChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility(field.name)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-white"
                      >
                        {showPasswords[field.name] ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ) : field.type === 'select' && field.options ? (
                    <select
                      value={formData.credentials[field.name] || ''}
                      onChange={(e) => handleCredentialChange(field.name, e.target.value)}
                      className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    >
                      <option value="">Selecione...</option>
                      {field.options.map((opt: { value: string; label: string }) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'boolean' ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.credentials[field.name] || false}
                        onChange={(e) => handleCredentialChange(field.name, e.target.checked)}
                        className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-sm text-surface-300">{field.description || 'Ativar'}</span>
                    </label>
                  ) : (
                    <input
                      type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
                      value={formData.credentials[field.name] || ''}
                      onChange={(e) => handleCredentialChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    />
                  )}
                  
                  {field.description && field.type !== 'boolean' && (
                    <p className="text-xs text-surface-500">{field.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Options */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-surface-300">Instância Ativa</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData((prev) => ({ ...prev, isDefault: e.target.checked }))}
              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-surface-300">Definir como Padrão</span>
          </label>
        </div>

        {/* Test Connection */}
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TestTube className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-medium text-white">Testar Conexão</span>
            </div>
            <button
              onClick={handleTestConnection}
              disabled={!selectedTemplateId || isTesting}
              className="flex items-center gap-2 px-3 py-1.5 bg-surface-700 text-white rounded-lg text-sm font-medium hover:bg-surface-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTesting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Testar
            </button>
          </div>

          {testResult && (
            <div
              className={cn(
                'flex items-start gap-2 p-3 rounded-lg',
                testResult.success
                  ? 'bg-green-500/10 border border-green-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              )}
            >
              {testResult.success ? (
                <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={cn(
                  'text-sm font-medium',
                  testResult.success ? 'text-green-400' : 'text-red-400'
                )}>
                  {testResult.success ? 'Conexão bem-sucedida!' : 'Falha na conexão'}
                </p>
                <p className="text-xs text-surface-400 mt-1">{testResult.message}</p>
                {testResult.latency && (
                  <p className="text-xs text-surface-500 mt-1">
                    Latência: {testResult.latency}ms
                  </p>
                )}
              </div>
            </div>
          )}

          {!testResult && !isEditing && (
            <p className="text-xs text-surface-500">
              Teste a conexão antes de salvar para garantir que as credenciais estão corretas.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-surface-300 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Salvar Alterações' : 'Criar Instância'}
          </button>
        </div>

        {/* Error */}
        {saveMutation.isError && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-sm text-red-400">
              {(saveMutation.error as any)?.response?.data?.message || 'Erro ao salvar instância'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InstanceConfigurator;
